import {
  dbui_v1_ObservableEventsList,
  DbuiV1GetObservableEventsData,
  dbuiV1GetObservableEventsInfiniteOptions,
  dbuiV1GetObservableEventsInfiniteQueryKey,
} from '@antimatterhq/asl-api-ts-v1';
import {
  InfiniteData,
  useInfiniteQuery,
} from '@antimatterhq/ui/hooks/queries/useInfiniteQuery';
import { useQueryClient } from '@antimatterhq/ui/hooks/queries/useQueryClient';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useWarehouseLocation } from '@/contexts/WarehouseLocationContext';
import { defaultOptions } from '@/utils/query';

type QueryOptions = Omit<
  NonNullable<DbuiV1GetObservableEventsData['query']>,
  'warehouse'
> & { kind?: string; value?: string };

export const useGetObservablesEvents = ({
  limit = 10,
  kind = '',
  cursor = '',
  value = '',
}: QueryOptions) => {
  const { workspace } = useWorkspace();
  const { selectedWarehouse } = useWarehouseLocation();
  const warehouse = selectedWarehouse?.id || '';

  return useInfiniteQuery({
    ...dbuiV1GetObservableEventsInfiniteOptions({
      ...defaultOptions(),
      path: { workspace, kind, value },
      query: { warehouse, cursor, limit },
    }),
    enabled: !!workspace || !!kind,
    getNextPageParam: (page) => page.cursor,
  });
};

export const useOptimisticUpdateObservablesEvents = ({
  limit = 10,
  kind = '',
  cursor = '',
  value = '',
}: QueryOptions) => {
  const { workspace } = useWorkspace();
  const { selectedWarehouse } = useWarehouseLocation();
  const warehouse = selectedWarehouse?.id || '';
  const queryClient = useQueryClient();

  return (observables: InfiniteData<dbui_v1_ObservableEventsList, unknown>) => {
    queryClient.setQueryData(
      dbuiV1GetObservableEventsInfiniteQueryKey({
        ...defaultOptions(),
        path: { workspace, kind, value },
        query: { warehouse, cursor, limit },
      }),
      observables,
    );
  };
};

export const useClearCacheObservablesEvents = () => {
  const { workspace } = useWorkspace();
  const { selectedWarehouse } = useWarehouseLocation();
  const warehouse = selectedWarehouse?.id || '';
  const queryClient = useQueryClient();
  return ({ kind, value }: { kind: string; value: string }) => {
    queryClient.removeQueries({
      queryKey: dbuiV1GetObservableEventsInfiniteQueryKey({
        ...defaultOptions(),
        path: { workspace, kind, value },
        query: { warehouse },
      }),
      exact: false,
    });
  };
};
